/** Opciones iconográficas — EPA «Procesa» (sin escritura libre).
 * Ramas por valencia: eje | carga | friccion — coherentes con Escucha / emoción.
 */

export const PERDONARSE_VALENCE = {
    EJE: 'eje',
    CARGA: 'carga',
    FRICCION: 'friccion',
};

export const PERDONARSE_INTRO = {
    title: 'Reconocer lo que sientes',
    body: 'Nombrar una emoción o sensación no es quejarse: le dice a tu cuerpo que ya la escuchaste. Eso baja la tensión y te ayuda a elegir el siguiente paso con más claridad.',
};

export const PERDONARSE_INTRO_BY_SOMATIC = {
    bien: {
        title: 'Si quieres, refuerza lo bueno',
        body: 'Dijiste que estás bien. Esto es opcional: puedes nombrar qué te sostiene hoy, o pasar si no hace falta.',
    },
    regular: {
        title: 'Nombrar la carga ayuda',
        body: 'Estás en esfuerzo. Ponerle nombre a lo que pesa (sin inventar un problema) ayuda a cuidar el ritmo.',
    },
    mal: {
        title: 'Reconocer lo que sientes',
        body: 'Si hubo un momento difícil, nombrarlo con respeto baja la tensión. No es reporte contra nadie.',
    },
    sick: {
        title: 'Cuida tu cuerpo primero',
        body: 'Indispuesto: prioriza seguridad. Si quieres, deja una señal breve de carga; no es obligatorio.',
    },
};

/** Emociones del paso 0 (compartidas). */
export const PERDONARSE_FEELINGS = [
    { key: 'calma', emoji: '😌', label: 'En calma', affirm: 'Bien por notar cuando estás estable. Eso también se cuida.', valence: 'eje' },
    { key: 'capaz', emoji: '💪', label: 'Capaz / con ganas', affirm: 'Reconocer tu energía positiva refuerza lo que ya haces bien.', valence: 'eje' },
    { key: 'cansado', emoji: '😴', label: 'Cansado/a', affirm: 'El cansancio es una señal honesta del cuerpo. Escucharla evita que se acumule.', valence: 'carga' },
    { key: 'frustrado', emoji: '😤', label: 'Frustrado/a', affirm: 'La frustración dice que algo importante para ti no salió como esperabas.', valence: 'friccion' },
    { key: 'nervioso', emoji: '😰', label: 'Nervioso/a', affirm: 'La tensión avisa que necesitas más tiempo, apoyo o claridad.', valence: 'friccion' },
    { key: 'triste', emoji: '😔', label: 'Triste o con pena', affirm: 'La tristeza merece espacio. Nombrarla ya es un acto de respeto hacia ti.', valence: 'friccion' },
    { key: 'molesto', emoji: '😠', label: 'Molesto/a', affirm: 'El enojo suele proteger un límite. Verlo con calma ayuda a no explotar.', valence: 'friccion' },
    { key: 'confundido', emoji: '🌀', label: 'Confundido/a', affirm: 'La confusión es normal cuando hay mucho a la vez. No estás fallando por sentirlo.', valence: 'friccion' },
];

const STEPS_BY_VALENCE = {
    eje: [
        {
            key: 'feeling',
            title: '¿Qué estás sintiendo ahora?',
            why: 'Cuando pones nombre a lo que sientes, el cerebro deja de pelear contra la señal. No es una etiqueta para siempre: es un momento que merece cuidado.',
        },
        {
            key: 'context',
            title: '¿Qué te ayuda a mantenerte así?',
            why: 'En eje no buscamos un problema: nombramos lo que te sostiene para poder cuidarlo.',
        },
        {
            key: 'takeaway',
            title: '¿Qué te llevas para adelante?',
            why: 'Una intención pequeña convierte la calma en hábito. No hace falta inventar una crisis.',
        },
    ],
    carga: [
        {
            key: 'feeling',
            title: '¿Qué estás sintiendo ahora?',
            why: 'Cuando pones nombre a lo que sientes, el cerebro deja de pelear contra la señal. No es una etiqueta para siempre: es un momento que merece cuidado.',
        },
        {
            key: 'context',
            title: '¿Qué pesa más hoy?',
            why: 'Nombrar la carga no es quejarse: es información para ajustar el ritmo a tiempo.',
        },
        {
            key: 'takeaway',
            title: '¿Qué te llevas para adelante?',
            why: 'Un paso pequeño protege tu energía sin frenar al equipo.',
        },
    ],
    friccion: [
        {
            key: 'feeling',
            title: '¿Qué estás sintiendo ahora?',
            why: 'Cuando pones nombre a lo que sientes, el cerebro deja de pelear contra la señal. No es una etiqueta para siempre: es un momento que merece cuidado.',
        },
        {
            key: 'context',
            title: '¿Qué rodeaba ese momento?',
            why: 'Entender el contexto no busca culpar a nadie. Te ayuda a ver si fue cansancio, prisa, ruido o algo del equipo — y qué puedes pedir distinto.',
        },
        {
            key: 'takeaway',
            title: '¿Qué te llevas para adelante?',
            why: 'Cerrar con una intención pequeña convierte la emoción en aprendizaje. No hace falta resolver todo hoy: basta con un paso que te respete.',
        },
    ],
};

/** @deprecated Usar getPerdonarseSteps(valence). Mantener para imports legacy. */
export const PERDONARSE_STEPS = STEPS_BY_VALENCE.friccion;

const CONTEXTS_BY_VALENCE = {
    eje: [
        { key: 'ritmo', emoji: '🌊', label: 'Buen ritmo', affirm: 'El ritmo adecuado sostiene el turno sin desgastarte.' },
        { key: 'equipo_ok', emoji: '🤝', label: 'El equipo acompaña', affirm: 'Sentir apoyo del equipo multiplica la calma.' },
        { key: 'tarea_clara', emoji: '📋', label: 'Tarea clara', affirm: 'La claridad baja la tensión antes de que aparezca.' },
        { key: 'descanso', emoji: '☕', label: 'Descansé bien', affirm: 'El descanso previo es parte del trabajo bien hecho.' },
        { key: 'nada', emoji: '✅', label: 'Nada especial', affirm: 'Está bien no forzar una historia. A veces solo estás bien.' },
        { key: 'omitir', emoji: '🤐', label: 'Prefiero no detallar', affirm: 'Tu límite también se respeta. Gracias por el registro.' },
    ],
    carga: [
        { key: 'sueno', emoji: '😴', label: 'Poco sueño', affirm: 'Dormir mal pesa en el cuerpo. Nombrarlo ya es cuidarte.' },
        { key: 'turno', emoji: '🕐', label: 'Turno largo / cambio', affirm: 'Los turnos dejan huella. Ajustar el ritmo es legítimo.' },
        { key: 'cuerpo', emoji: '🦴', label: 'El cuerpo pide pausa', affirm: 'El cuerpo avisa antes de que falle el foco.' },
        { key: 'carga', emoji: '📦', label: 'Mucha carga', affirm: 'La cantidad también es información. No todo es “actitud”.' },
        { key: 'nada', emoji: '✅', label: 'Nada claro / nada especial', affirm: 'A veces el cansancio no tiene una sola causa. Está bien.' },
        { key: 'omitir', emoji: '🤐', label: 'Prefiero no detallar', affirm: 'Tu límite también se respeta. Gracias por el registro.' },
    ],
    friccion: [
        { key: 'prisa', emoji: '⏱️', label: 'Mucha prisa', affirm: 'La prisa estresa el cuerpo. Identificarla es el primer paso para bajar el ritmo.' },
        { key: 'cansancio', emoji: '🪫', label: 'Poca energía', affirm: 'Trabajar agotado multiplica errores. Tu cansancio es información útil.' },
        { key: 'ruido', emoji: '🔊', label: 'Ruido o distracción', affirm: 'El entorno importa. No todo depende de tu voluntad.' },
        { key: 'equipo', emoji: '👥', label: 'Algo con el equipo', affirm: 'Las relaciones en planta afectan cómo nos sentimos. Nombrarlo abre conversación.' },
        { key: 'proceso', emoji: '📋', label: 'Proceso poco claro', affirm: 'Sin claridad, el cuerpo se tensa. Pedir instrucciones es legítimo.' },
        { key: 'equipo_falla', emoji: '🔧', label: 'Falla de máquina o material', affirm: 'Los tropiezos operativos generan estrés. No tienes que cargar todo solo.' },
        { key: 'nada', emoji: '✅', label: 'Nada en particular', affirm: 'No hace falta inventar un conflicto. Gracias por honestidad.' },
        { key: 'omitir', emoji: '🤐', label: 'Prefiero no detallar', affirm: 'Tu límite también se respeta. Gracias por el registro.' },
    ],
};

/** @deprecated Usar getPerdonarseContexts(valence). */
export const PERDONARSE_CONTEXTS = CONTEXTS_BY_VALENCE.friccion;

const TAKEAWAYS_BY_VALENCE = {
    eje: [
        { key: 'cuidar_ritmo', emoji: '🌊', label: 'Cuidar este ritmo', affirm: 'Mantener lo que funciona también es maestría.' },
        { key: 'agradecer', emoji: '🙏', label: 'Agradecer lo que ayuda', affirm: 'Notar el apoyo refuerza el clima del equipo.' },
        { key: 'compartir', emoji: '💬', label: 'Compartir lo que funciona', affirm: 'Lo que te sostiene puede servirle a otro.' },
        { key: 'seguir', emoji: '🌱', label: 'Seguir sin forzarme', affirm: 'La calma se cuida sin inventar drama.' },
        { key: 'omitir', emoji: '🤐', label: 'Prefiero no detallar', affirm: 'Listo. Queda el registro sin presión.' },
    ],
    carga: [
        { key: 'pausa', emoji: '🧘', label: 'Hacerme una pausa', affirm: 'Una pausa breve puede devolverte foco sin frenar al equipo.' },
        { key: 'cuidarme', emoji: '❤️', label: 'Cuidar mi ritmo hoy', affirm: 'Ajustar tu ritmo hoy protege tu salud y tu trabajo de mañana.' },
        { key: 'ayuda', emoji: '🤝', label: 'Pedir ayuda a tiempo', affirm: 'Pedir apoyo a tiempo es maestría, no debilidad.' },
        { key: 'seguir', emoji: '🌱', label: 'Seguir sin castigarme', affirm: 'El cansancio no es fallo moral. Se nombra y se cuida.' },
        { key: 'omitir', emoji: '🤐', label: 'Prefiero no detallar', affirm: 'Listo. Queda el registro sin presión.' },
    ],
    friccion: [
        { key: 'pausa', emoji: '🧘', label: 'Hacerme una pausa', affirm: 'Una pausa breve puede devolverte foco sin frenar al equipo.' },
        { key: 'ayuda', emoji: '🤝', label: 'Pedir ayuda a tiempo', affirm: 'Pedir apoyo a tiempo es maestría, no debilidad.' },
        { key: 'hablar', emoji: '💬', label: 'Hablarlo con calma', affirm: 'Una conversación serena evita que la emoción se quede guardada.' },
        { key: 'revisar', emoji: '🔍', label: 'Revisar el paso a paso', affirm: 'Volver al procedimiento con calma reduce la repetición del error.' },
        { key: 'cuidarme', emoji: '❤️', label: 'Cuidar mi ritmo hoy', affirm: 'Ajustar tu ritmo hoy protege tu salud y tu trabajo de mañana.' },
        { key: 'seguir', emoji: '🌱', label: 'Seguir sin castigarme', affirm: 'Perdonarte el momento libera energía para hacerlo mejor después.' },
        { key: 'omitir', emoji: '🤐', label: 'Prefiero no detallar', affirm: 'Listo. Queda el registro sin presión.' },
    ],
};

/** @deprecated Usar getPerdonarseTakeaways(valence). */
export const PERDONARSE_TAKEAWAYS = TAKEAWAYS_BY_VALENCE.friccion;

export function valenceFromFeelingKey(feelingKey) {
    const hit = PERDONARSE_FEELINGS.find((o) => o.key === feelingKey);
    if (hit?.valence) return hit.valence;
    return PERDONARSE_VALENCE.FRICCION;
}

/** Mapea check-in somático / llegada a banda sugerida (no bloquea elección). */
export function valenceFromSomaticFeeling(somaticFeeling) {
    if (somaticFeeling === 'bien') return PERDONARSE_VALENCE.EJE;
    if (somaticFeeling === 'regular' || somaticFeeling === 'sick') return PERDONARSE_VALENCE.CARGA;
    if (somaticFeeling === 'mal') return PERDONARSE_VALENCE.FRICCION;
    return null;
}

export function getPerdonarseIntro(somaticFeeling) {
    if (somaticFeeling && PERDONARSE_INTRO_BY_SOMATIC[somaticFeeling]) {
        return PERDONARSE_INTRO_BY_SOMATIC[somaticFeeling];
    }
    return PERDONARSE_INTRO;
}

export function getPerdonarseSteps(valence) {
    return STEPS_BY_VALENCE[valence] || STEPS_BY_VALENCE.friccion;
}

export function getPerdonarseContexts(valence) {
    return CONTEXTS_BY_VALENCE[valence] || CONTEXTS_BY_VALENCE.friccion;
}

export function getPerdonarseTakeaways(valence) {
    return TAKEAWAYS_BY_VALENCE[valence] || TAKEAWAYS_BY_VALENCE.friccion;
}

export function getPerdonarseOptionsForStep(stepIndex, feelingKey) {
    const valence = stepIndex === 0 ? null : valenceFromFeelingKey(feelingKey);
    if (stepIndex === 0) return PERDONARSE_FEELINGS;
    if (stepIndex === 1) return getPerdonarseContexts(valence);
    return getPerdonarseTakeaways(valence);
}

export function getPerdonarseStepMeta(stepIndex, feelingKey) {
    if (stepIndex === 0) {
        return getPerdonarseSteps(PERDONARSE_VALENCE.FRICCION)[0];
    }
    const valence = valenceFromFeelingKey(feelingKey);
    return getPerdonarseSteps(valence)[stepIndex];
}

export function mapPerdonarseToIncident({ feelingKey, contextKey, takeawayKey }) {
    const valence = valenceFromFeelingKey(feelingKey);
    const feeling = PERDONARSE_FEELINGS.find((o) => o.key === feelingKey);
    const context = getPerdonarseContexts(valence).find((o) => o.key === contextKey);
    const takeaway = getPerdonarseTakeaways(valence).find((o) => o.key === takeawayKey);

    const kindLabel =
        valence === 'eje' ? 'Sostén' : valence === 'carga' ? 'Carga' : 'Sensación';

    return {
        whatHappened: feeling ? `${kindLabel}: ${feeling.label}` : '',
        whyItHappened: context ? `Contexto: ${context.label}` : null,
        lessonLearned: takeaway ? takeaway.label : null,
        isAnonymous: true,
        valence,
        kind: valence === 'eje' ? 'refuerzo' : valence === 'carga' ? 'carga' : 'friccion',
    };
}

export function successMessageForValence(valence) {
    if (valence === 'eje') return 'Gracias por notar lo que te sostiene.';
    if (valence === 'carga') return 'Gracias por nombrar la carga con respeto.';
    return 'Registrado con respeto. El error es dato, no sentencia.';
}
